import React from 'react';
import { LegalPageLayout, LegalSection, LegalCallout } from '../components/LegalPageLayout';

const TOC = [
  { id: 'sec-1', label: 'Acceptance of Terms' },
  { id: 'sec-2', label: 'Description of Service; Our Role' },
  { id: 'sec-3', label: 'Account Registration' },
  { id: 'sec-4', label: 'Operator Responsibilities' },
  { id: 'sec-5', label: 'Setup Assistance & Support' },
  { id: 'sec-6', label: 'Pricing, Payments & Financial Role' },
  { id: 'sms-terms', label: 'SMS/Text Message Terms' },
  { id: 'sec-8', label: 'Digital Waivers' },
  { id: 'sec-9', label: 'Operator–Customer Relationship' },
  { id: 'sec-10', label: 'Acceptable Use' },
  { id: 'sec-11', label: 'Service Availability' },
  { id: 'sec-12', label: 'Intellectual Property' },
  { id: 'sec-13', label: 'Disclaimer of Warranties' },
  { id: 'sec-14', label: 'Limitation of Liability' },
  { id: 'sec-15', label: 'Indemnification' },
  { id: 'sec-16', label: 'International Use; USA & UAE' },
  { id: 'sec-17', label: 'No Professional Advice' },
  { id: 'sec-18', label: 'Force Majeure' },
  { id: 'sec-19', label: 'Termination' },
  { id: 'sec-20', label: 'Changes to Terms' },
  { id: 'beta', label: 'Beta Features' },
  { id: 'ai', label: 'AI Features; No Guaranteed Outcomes' },
  { id: 'api', label: 'API Access' },
  { id: 'third-party', label: 'Third-Party Services & Integrations' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'sanctions', label: 'Sanctions & Export Controls' },
  { id: 'assignment', label: 'Assignment' },
  { id: 'sec-21', label: 'Governing Law & Disputes' },
  { id: 'sec-22', label: 'Contact' },
];

const TermsOfService = () => (
  <LegalPageLayout title="Terms of Service" lastUpdated="August 5, 2026" toc={TOC}>
    <LegalSection id="sec-1" number={1} title="Acceptance of Terms">
      <p>
        By accessing and using BookingFlows, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our service. In these Terms, &quot;Operator&quot; means a business that uses BookingFlows to offer, schedule, and sell its services; &quot;Customer&quot; means an end user who books an Operator&apos;s services through the platform.
      </p>
    </LegalSection>

    <LegalSection id="sec-2" number={2} title="Description of Service; Our Role">
      <p>
        BookingFlows is a booking and scheduling <strong>software platform</strong> designed for tours, rentals, charters, classes, venues, and service-based businesses. We provide:
      </p>
      <ul>
        <li>Online booking widgets and scheduling tools</li>
        <li>Payment processing facilitation through Stripe</li>
        <li>Digital waiver collection with verification</li>
        <li>Staff scheduling and coordination</li>
        <li>Customer relationship management</li>
      </ul>
      <LegalCallout>
        <strong>BookingFlows is a technology provider only.</strong> We are not a tour operator, travel agent, charter company, rental company, insurer, employer of any Operator&apos;s staff, or merchant of record for any Operator&apos;s sales. We are not a party to any transaction, booking, or contract between an Operator and a Customer. Each booking made through the platform is a direct contract between the Operator and the Customer, and the Operator is solely responsible for providing the booked services.
      </LegalCallout>
    </LegalSection>

    <LegalSection id="sec-3" number={3} title="Account Registration">
      <p>To use BookingFlows, you must create an account with accurate information. You are responsible for:</p>
      <ul>
        <li>Maintaining the security of your account credentials</li>
        <li>All activities that occur under your account, including actions of your team members</li>
        <li>Notifying us immediately of unauthorized access</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-4" number={4} title="Operator Responsibilities">
      <p>Operators are solely responsible for their business and its operation, including without limitation:</p>
      <ul>
        <li><strong>Configuration accuracy.</strong> The accuracy and completeness of everything the Operator configures or has configured on its behalf in the platform — services, pricing, taxes and fees, schedules, capacity, availability, add-ons, policies, waiver text, and all published content. Operators must review and verify their configuration before going live and after any change.</li>
        <li><strong>Service delivery.</strong> The quality, safety, legality, and delivery of the services sold to Customers, including vessel/vehicle/equipment condition, crew competence, and appropriate staffing.</li>
        <li><strong>Licenses and permits.</strong> Holding and maintaining every license, permit, registration, certification, and insurance policy required to operate — including, as applicable, business licenses, captain/crew licenses, maritime and coast guard requirements, tourism authority licensing, and local operating permits in each jurisdiction where the Operator does business (including the United States and the United Arab Emirates).</li>
        <li><strong>Legal compliance.</strong> Compliance with all laws applicable to the Operator&apos;s business, including consumer protection, safety regulations, employment law, marketing and communications laws (e.g., TCPA in the USA and equivalent rules in the UAE), and data protection laws with respect to the Customer data the Operator collects and controls.</li>
        <li><strong>Taxes.</strong> Determining, configuring, collecting, reporting, and remitting all applicable taxes on the Operator&apos;s sales — including U.S. sales and local taxes and UAE VAT. BookingFlows provides tax configuration tools as a convenience but does not calculate, warrant, or remit taxes on any Operator&apos;s behalf and provides no tax advice.</li>
        <li><strong>Refunds, cancellations, and disputes.</strong> Setting and honoring the Operator&apos;s own refund and cancellation policies, and resolving all Customer disputes, complaints, chargebacks, and claims relating to the Operator&apos;s services.</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-5" number={5} title="Setup Assistance and Support">
      <p>
        Our team may assist Operators with onboarding, data migration, and platform configuration as a courtesy. Any such assistance is provided on an &quot;as instructed&quot; and &quot;as is&quot; basis:
      </p>
      <ul>
        <li>Setup assistance does not transfer responsibility. The Operator remains solely responsible for reviewing and verifying that all configured services, prices, taxes, schedules, capacities, and policies accurately reflect its actual business operations before accepting bookings.</li>
        <li>BookingFlows is not liable for any loss arising from configurations that do not accurately reflect the Operator&apos;s operations, pricing, or policies, whether configured by the Operator or with our assistance.</li>
        <li>Suggestions, templates, and sample content (including sample waiver text) are provided for convenience only and do not constitute legal, tax, financial, insurance, or professional advice of any kind.</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-6" number={6} title="Pricing, Payments, and Financial Role">
      <p>BookingFlows operates on a transaction-based fee model:</p>
      <ul>
        <li>No monthly fees or setup costs</li>
        <li>3% platform fee on customer bookings (paid by customer)</li>
        <li>Payment processing handled securely through Stripe</li>
        <li>Funds are deposited directly to your own Stripe account</li>
      </ul>
      <LegalCallout>
        <strong>We do not handle your money.</strong> Each Operator maintains its own Stripe (or other PCI-compliant processor) account, and all Customer payments flow directly from the Customer to the Operator&apos;s own processor account. BookingFlows never holds, custodies, controls, or transmits Operator or Customer funds, is not a bank, money transmitter, money services business, or payment processor, and does not provide escrow. Card data is collected and processed exclusively by Stripe under its own PCI DSS certification and <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer">terms</a>; BookingFlows does not store full card numbers. Chargebacks, refunds, processor fees, processor account standing, payout timing, and any disputes with the processor are strictly between the Operator, the Customer, and Stripe.
      </LegalCallout>
      <p>We reserve the right to modify our pricing with 30 days notice.</p>
    </LegalSection>

    <LegalSection id="sms-terms" number={7} title="SMS/Text Message Terms">
      <p>
        By providing your mobile phone number and completing a booking or creating an account on BookingFlows, you expressly consent to receive SMS/text messages for transactional purposes including:
      </p>
      <ul>
        <li>Booking confirmations and reminders</li>
        <li>One-time passwords (OTP) and two-factor authentication (2FA)</li>
        <li>Schedule updates and reservation changes</li>
        <li>Account notifications and security alerts</li>
      </ul>
      <LegalCallout>
        <strong>Message frequency varies</strong> based on your booking activity. <strong>Message and data rates may apply.</strong> Reply <strong>STOP</strong> to opt out. Reply <strong>HELP</strong> for help. For questions, visit our <a href="/support">Support Center</a>.
      </LegalCallout>
      <p>
        SMS messages are <strong>not used for promotional or marketing purposes</strong>. By opting in, you agree to our <a href="/privacy#sms-terms">SMS Privacy Policy</a>.
      </p>
    </LegalSection>

    <LegalSection id="sec-8" number={8} title="Digital Waivers">
      <p>
        Our digital waiver system collects device fingerprints, IP addresses, and signature data for legal verification. By using this feature:
      </p>
      <ul>
        <li>You agree to comply with all applicable laws regarding liability waivers</li>
        <li>You are responsible for the legal validity, enforceability, and content of your waiver text in every jurisdiction where you operate</li>
        <li>We provide the technology; you are responsible for legal compliance</li>
        <li>We make no representation that any waiver signed through the platform will be enforceable, and we recommend consulting with legal counsel for waiver content</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-9" number={9} title="Operator–Customer Relationship; Assumption of Risk">
      <p>All bookings are contracts formed directly between the Operator and the Customer. Accordingly:</p>
      <ul>
        <li>BookingFlows has no responsibility or liability for the acts, errors, omissions, representations, warranties, breaches, or negligence of any Operator, or for any personal injury, death, property damage, or other damages or expenses arising from an Operator&apos;s services.</li>
        <li>Many activities offered by Operators (boating, watersports, tours, adventure experiences) carry inherent risk. Customers participate at their own risk and subject to the Operator&apos;s terms, waivers, and safety instructions.</li>
        <li>BookingFlows is not responsible for cancellations, no-shows, weather events, itinerary changes, service quality, or fitness of any Operator&apos;s services for a particular purpose.</li>
        <li>Any claim a Customer has in connection with a booked service must be brought against the Operator, not BookingFlows.</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-10" number={10} title="Acceptable Use">
      <p>You agree not to:</p>
      <ul>
        <li>Use the service for any illegal purpose, including phishing, spam, or fraud</li>
        <li>Attempt to access unauthorized data or accounts, or share account or API credentials with third parties</li>
        <li>Interfere with the proper functioning of the service, including denial-of-service attacks or imposing abnormal load</li>
        <li>Upload malicious code or viruses</li>
        <li>Scrape, harvest, or bulk-extract data without permission</li>
        <li>Reverse engineer, decompile, or attempt to derive the source code of the platform</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-11" number={11} title="Service Availability">
      <p>We strive for 99.9% uptime but do not guarantee uninterrupted service. We are not liable for:</p>
      <ul>
        <li>Service interruptions or downtime</li>
        <li>Data loss (though we maintain regular encrypted backups)</li>
        <li>Third-party service failures (Stripe, Twilio, hosting providers, carriers, etc.)</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-12" number={12} title="Intellectual Property">
      <p>
        BookingFlows and its original content, features, and functionality are owned by BookingFlows and are protected by copyright, trademark, and other laws. Your data and customer information remain your property. The platform may include components licensed under open-source licenses; those licenses continue to govern the relevant components.
      </p>
    </LegalSection>

    <LegalSection id="sec-13" number={13} title="Disclaimer of Warranties">
      <p>
        THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WITHOUT LIMITATION IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE, SECURE, OR UNINTERRUPTED, THAT DEFECTS WILL BE CORRECTED, OR THAT ANY CONFIGURATION, CALCULATION, TAX SETTING, OR CONTENT ON THE PLATFORM ACCURATELY REFLECTS ANY OPERATOR&apos;S ACTUAL BUSINESS OPERATIONS.
      </p>
    </LegalSection>

    <LegalSection id="sec-14" number={14} title="Limitation of Liability">
      <p>
        To the maximum extent permitted by applicable law, BookingFlows and its owners, officers, employees, and agents shall not be liable for:
      </p>
      <ul>
        <li>Lost revenue, lost profits, or lost bookings</li>
        <li>Indirect, incidental, special, consequential, exemplary, or punitive damages of any kind</li>
        <li>Customer disputes, chargebacks, or legal claims relating to an Operator&apos;s services</li>
        <li>Personal injury, death, or property damage arising from services provided by Operators</li>
        <li>Waiver validity or legal enforceability</li>
        <li>Inaccurate platform configuration, including pricing, taxes, capacity, or scheduling, regardless of who performed the configuration</li>
        <li>Third-party service failures, including payment processors, SMS/email carriers, and hosting providers</li>
      </ul>
      <p>
        OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE PLATFORM FEES WE ACTUALLY RECEIVED FROM YOUR ACCOUNT IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS (US $100). Some jurisdictions do not allow the exclusion of certain warranties or limitation of certain damages; where such laws apply to you, portions of the above may not apply, and our liability is limited to the greatest extent permitted by law. Nothing in these Terms excludes liability that cannot be excluded under applicable law.
      </p>
    </LegalSection>

    <LegalSection id="sec-15" number={15} title="Indemnification">
      <p>
        You (the Operator) agree to defend, indemnify, and hold harmless BookingFlows and its owners, officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys&apos; fees) arising out of or in any way connected with: (a) the services you offer or provide to Customers; (b) any personal injury, death, or property damage occurring in connection with your services; (c) your platform configuration or published content; (d) your violation of any law or the rights of a third party (including Customers, employees, and tax or licensing authorities); (e) your taxes; or (f) your breach of these Terms.
      </p>
    </LegalSection>

    <LegalSection id="sec-16" number={16} title="International Use; USA and UAE">
      <p>
        BookingFlows serves Operators and Customers in multiple jurisdictions, including the United States and the United Arab Emirates. Operators are solely responsible for compliance with the laws of every jurisdiction in which they operate or offer services, including without limitation:
      </p>
      <ul>
        <li><strong>USA:</strong> federal, state, and local business licensing, U.S. Coast Guard and maritime requirements, sales tax, consumer protection, and communications laws.</li>
        <li><strong>UAE:</strong> trade licensing, tourism authority requirements (e.g., DET/DTCM where applicable), maritime authority requirements, UAE VAT registration and remittance, and UAE consumer protection law.</li>
      </ul>
      <p>
        Nothing in these Terms limits mandatory statutory rights granted to consumers under applicable local law where those rights cannot be waived by contract.
      </p>
    </LegalSection>

    <LegalSection id="sec-17" number={17} title="No Professional Advice">
      <p>
        Nothing provided through the platform or by our team — including setup assistance, templates, sample waivers, tax configuration tools, reports, or support communications — constitutes legal, tax, accounting, financial, insurance, or safety advice. You should consult qualified professionals licensed in your jurisdiction for such matters.
      </p>
    </LegalSection>

    <LegalSection id="sec-18" number={18} title="Force Majeure">
      <p>
        BookingFlows shall not be liable for any failure or delay in performance caused by events beyond our reasonable control, including acts of God, natural disasters, weather events, epidemics, war, terrorism, labor disputes, governmental action, internet or utility failures, or failures of third-party providers.
      </p>
    </LegalSection>

    <LegalSection id="sec-19" number={19} title="Termination">
      <p>
        You may cancel your account at any time. We may suspend or terminate your account if you violate these terms. Upon termination:
      </p>
      <ul>
        <li>Your access to the service will be disabled</li>
        <li>We will retain your data for 30 days before deletion</li>
        <li><strong>You can export your data during this period</strong> — your booking, customer, and financial records are available for export from the dashboard or on request to support</li>
      </ul>
    </LegalSection>

    <LegalSection id="sec-20" number={20} title="Changes to Terms">
      <p>
        We may modify these terms at any time. We will notify you of material changes via email. Continued use of the service after changes constitutes acceptance of the new terms.
      </p>
    </LegalSection>

    <LegalSection id="beta" number={21} title="Beta Features">
      <p>
        Features identified as &quot;beta,&quot; &quot;preview,&quot; &quot;early access,&quot; or similar (including, without limitation, BookingFlows Finance and other features so designated) are provided for evaluation. Beta features may be modified, suspended, or removed at any time without notice, are provided &quot;as is&quot; without service level guarantees, and may be subject to additional terms. You use beta features at your own discretion and risk.
      </p>
    </LegalSection>

    <LegalSection id="ai" number={22} title="AI Features; No Guaranteed Outcomes">
      <p>
        The platform includes AI-assisted features (including HELM Intelligence suggestions and the Bosun assistant). AI-generated content, insights, and recommendations are <strong>informational only</strong>, may be inaccurate or incomplete, and do not constitute legal, accounting, financial, tax, safety, navigational, or operational advice. You should independently verify any output before relying on it.
      </p>
      <p>
        BookingFlows does not guarantee any increase in bookings, revenue, profitability, search rankings, advertising performance, or any other business outcome through use of the platform, including its analytics, marketing, and AI features.
      </p>
    </LegalSection>

    <LegalSection id="api" number={23} title="API Access">
      <p>
        API access is provided subject to these Terms and the applicable documentation. Rate limits apply to ensure fair usage for all customers. We may suspend or revoke API access for abuse, excessive load, credential sharing, security concerns, or activity that threatens platform stability. We may modify API endpoints over time; where reasonably practicable, we will provide notice of breaking changes.
      </p>
    </LegalSection>

    <LegalSection id="third-party" number={24} title="Third-Party Services and Integrations">
      <p>
        The platform interoperates with third-party services (including, for example, Stripe, Twilio, Zapier, Intuit QuickBooks, weather data providers, and hosting providers). BookingFlows is not responsible for changes made by third-party providers — including API changes, pricing changes, service interruptions, feature removals, or discontinued services — and may modify or discontinue an integration if a provider&apos;s changes make it impractical to maintain. Your use of third-party services is governed by those providers&apos; own terms and privacy policies.
      </p>
    </LegalSection>

    <LegalSection id="feedback" number={25} title="Feedback">
      <p>
        If you submit ideas, suggestions, or other feedback about the service, BookingFlows may use that feedback without restriction or compensation, and without any obligation to you.
      </p>
    </LegalSection>

    <LegalSection id="sanctions" number={26} title="Sanctions and Export Controls">
      <p>
        You may not use the service in violation of applicable sanctions or export control laws, including those of the United States and the United Arab Emirates, and you represent that you are not located in, or acting on behalf of, any sanctioned country, entity, or person.
      </p>
    </LegalSection>

    <LegalSection id="assignment" number={27} title="Assignment">
      <p>
        You may not assign these Terms without our prior written consent. BookingFlows may assign these Terms in connection with a merger, acquisition, corporate restructuring, or sale of all or substantially all of its assets.
      </p>
    </LegalSection>

    <LegalSection id="sec-21" number={28} title="Governing Law and Dispute Resolution">
      <LegalCallout>
        <strong>Support first.</strong> Before initiating any formal dispute, you agree to first contact us through the BookingFlows <a href="/support">Support Center</a> and allow us thirty (30) days to work with you toward an informal resolution. Most issues are resolved this way — quickly and at no cost to anyone.
      </LegalCallout>
      <p>
        These Terms are governed by the laws of the <strong>State of Wyoming, United States of America</strong>, without regard to its conflict of laws principles. Any dispute arising out of or relating to these Terms or the service shall be resolved through binding, individual arbitration conducted in Wyoming (or remotely by mutual agreement), and <strong>you waive any right to participate in a class action or class-wide arbitration</strong>. Either party may bring qualifying claims in small-claims court. For Operators and Customers located in the UAE or other jurisdictions, mandatory protections of local law that cannot be contractually waived remain unaffected.
      </p>
    </LegalSection>

    <LegalSection id="sec-22" number={29} title="Contact">
      <p>For questions about these Terms of Service, reach us through our Support Center:</p>
      <p className="font-semibold text-gray-800"><a href="/support#tickets">bookingflows.com/support</a></p>
    </LegalSection>
  </LegalPageLayout>
);

export default TermsOfService;
